import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Chip, IconButton, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { PayrollService } from '../../services/payroll.service';
import { IEmployeeSalary, ISalaryComponent } from '../../interfaces/payroll.interface';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import EmployeeSalaryForm from './EmployeeSalaryForm';
import { usePayrollInstitution } from '../../hooks/usePayrollInstitution';
import { formatDate } from '../../utils/dateUtils';
import { displayUsername } from '../../utils/userUtils';

const EmployeeSalaryList: React.FC = () => {
    const [salaries, setSalaries] = useState<IEmployeeSalary[]>([]);
    const [components, setComponents] = useState<ISalaryComponent[]>([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingSalary, setEditingSalary] = useState<IEmployeeSalary | null>(null);
    const { t } = useTranslation(['payroll', 'common']);
    const { showSuccess, showApiError } = useToast();
    const { isSystemAdmin, institutions, institutionId, setInstitutionId } = usePayrollInstitution();

    const loadData = async () => {
        if (!institutionId) return;
        try {
            const [salaryData, componentData] = await Promise.all([
                PayrollService.getEmployeeSalaries(institutionId),
                PayrollService.getSalaryComponents(institutionId)
            ]);
            setSalaries(salaryData);
            setComponents(componentData);
        } catch (error) {
            showApiError(error, t('employeeSalaryList.toast.loadFailed'));
        }
    };

    useEffect(() => { loadData(); }, [institutionId]);

    const handleAdd = () => {
        setEditingSalary(null);
        setDialogOpen(true);
    };

    const handleEdit = (salary: IEmployeeSalary) => {
        setEditingSalary(salary);
        setDialogOpen(true);
    };

    const handleSaved = () => {
        setDialogOpen(false);
        showSuccess(t('employeeSalaryList.toast.saved'));
        loadData();
    };

    const getTotalSalary = (salary: IEmployeeSalary) => {
        const earnings = salary.details
            .filter(d => d.componentType === 'EARNING')
            .reduce((sum, d) => sum + d.amount, 0);
        const deductions = salary.details
            .filter(d => d.componentType === 'DEDUCTION')
            .reduce((sum, d) => sum + d.amount, 0);
        return { earnings, deductions, net: earnings - deductions };
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">{t('employeeSalaryList.title')}</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {isSystemAdmin && (
                        <FormControl size="small" sx={{ minWidth: 220 }}>
                            <InputLabel>{t('shared.selectInstitution')}</InputLabel>
                            <Select
                                value={institutionId}
                                onChange={(e) => setInstitutionId(e.target.value)}
                                label={t('shared.selectInstitution')}
                            >
                                {institutions.map((inst) => (
                                    <MenuItem key={inst.id} value={inst.id}>{inst.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <Button variant="contained" color="primary" onClick={handleAdd} disabled={!institutionId}>
                        {t('employeeSalaryList.assign')}
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t('shared.employee')}</TableCell>
                            <TableCell>{t('employeeSalaryList.effectiveFrom')}</TableCell>
                            <TableCell align="right">{t('employeeSalaryList.totalEarnings')}</TableCell>
                            <TableCell align="right">{t('employeeSalaryList.totalDeductions')}</TableCell>
                            <TableCell align="right">{t('shared.netSalary')}</TableCell>
                            <TableCell>{t('common:fields.status')}</TableCell>
                            <TableCell align="right">{t('common:fields.actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {salaries.map((salary) => {
                            const totals = getTotalSalary(salary);
                            return (
                                <TableRow key={salary.id}>
                                    <TableCell>{displayUsername(salary.userName)}</TableCell>
                                    <TableCell>{salary.effectiveFrom ? formatDate(salary.effectiveFrom) : '-'}</TableCell>
                                    <TableCell align="right">{totals.earnings.toFixed(2)}</TableCell>
                                    <TableCell align="right">{totals.deductions.toFixed(2)}</TableCell>
                                    <TableCell align="right">{totals.net.toFixed(2)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={salary.isActive ? t('common:status.active') : t('common:status.inactive')}
                                            color={salary.isActive ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => handleEdit(salary)} size="small" aria-label={t('employeeSalaryList.edit')}>
                                            <EditIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {salaries.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={7} align="center">{t('employeeSalaryList.empty')}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <EmployeeSalaryForm
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSaved={handleSaved}
                salary={editingSalary}
                components={components}
            />
        </Box>
    );
};

export default EmployeeSalaryList;
